import { flatMap, groupBy, map, size, sortBy, startCase, uniq, uniqBy, words } from 'lodash';
import { action, computed, observable } from 'mobx';
import { toStream } from 'mobx-utils';
import { from } from 'rxjs';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { tap } from 'rxjs/internal/operators/tap';
import { filter } from 'rxjs/operators';
import { getViewCount$ } from '../utils/youtube';
import { SpeakerData, SpeechData } from './SpeechData';

export const filterTruthy = filter(value => Boolean(value));
export const getObservable = getter => from(toStream(getter, true)).pipe(filterTruthy);

export const FUTURE_SPEECHES_KEYWORD = 'soon';

export class SpeechesData {
    @observable isLoading: boolean;
    @observable viewCountLoading: boolean;
    @observable viewCount: number;
    @observable private data: SpeechData[] = [];
    @action private updateSpeeches = speeches => {
        this.data = uniqBy(speeches, JSON.stringify).map(data => new SpeechData(data, this));
        this.isLoading = false;
    };

    constructor() {
        this.isLoading = true;
        fetch('/speeches.json')
            .then(response => response.json())
            .then(this.updateSpeeches);

        const stat$ = getObservable(() => this.videoIds).pipe(
            filter(ids => size(ids) > 0),
            tap(action(() => {this.viewCountLoading = true;})),
            switchMap(getViewCount$),
        );

        stat$.subscribe(stats => {
            action(() => {
                this.viewCountLoading = false;
                this.viewCount = stats;
            })();
        });
    }

    @computed get sorted(): SpeechData[] {
        return sortBy(this.data, (speech: SpeechData) => speech.moment.utc()).reverse();
    }

    @computed get byYear(): { [year: string]: SpeechData[] } {
        return groupBy(this.sorted, (speech: SpeechData) => speech.year);
    }

    @computed get years(): string[] {
        return Object.keys(this.byYear).sort();
    }

    @computed get countries(): string[] {
        return uniq(map(this.data, (speech: SpeechData) => speech.Country)).filter(Boolean);
    }

    @computed get countriesFlags(): { [country: string]: Promise<string> } {
        return this.countries
            .map(country => {
                const name = words(country).map(startCase).join('-');
                const path = SpeechesData.preloadFlag(name);
                return ({ [country]: path });
            })
            .reduce((obj, country) => Object.assign(obj, country), {});
    }

    @computed get locations(): string[] {
        return uniq(map(this.data, (speech: SpeechData) => speech.Location)).filter(Boolean);
    }

    @computed get speakers(): SpeakerData[] {
        const speakers = flatMap(this.data, (speech: SpeechData) => speech.Speakers);
        return uniqBy(speakers, (speaker: SpeakerData) => speaker.name);
    }

    @computed get speakersPhotos(): { [speaker: string]: Promise<string> } {
        return this.speakers
            .map(speaker => ({ [speaker.name]: SpeechesData.preloadPhoto(speaker) }))
            .reduce((obj, country) => Object.assign(obj, country), {});
    }

    @computed get videoIds() {
        return flatMap(this.data, (speech: SpeechData) => speech.youtubeIds);
    }

    private static async preloadImg(path: string) {
        const { default: photo } = await import(`img/${path}`);
        const img = new Image();
        return img.src = photo;
    }

    private static async preloadFlag(country: string): Promise<string> {
        if (!country) return;
        try {
            return await SpeechesData.preloadImg(`flags/${country}.png`);
        } catch (error) {
            console.warn(`no flag for ${country}`);
        }
    }

    private static async preloadPhoto(speaker: SpeakerData): Promise<string> {
        if (!speaker.photo) return;
        try {
            return await SpeechesData.preloadImg(`people/conf/${speaker.photo}`);
        } catch (error) {
            console.warn(`wrong image for ${speaker.name}`);
        }
    }

}
